import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Modal, Table } from 'antd';
import { color } from 'asserts/styles/colors';
import { font } from 'asserts/styles/fonts';
import DefaultButton from 'components/button/DefaultButton';
import RadioButton from 'components/button/RadioButton';
import { commonSubScribeColumns } from 'consts/table';
import DefaultInput from 'components/input/DefaultInput';
import { useMutation } from '@tanstack/react-query';
import { restfetcher } from 'queryClient';
import modalInformationAtom from 'atom/modalInformationAtom';
import { useAtomValue, useSetAtom } from 'jotai';
import companyInforAtom from 'atom/companyInfoAtom';
import userInforAtom from 'atom/userInfoAtom';

interface UnsubscribeModalProps {
  open: boolean;
  unSubScribeData: {
    subscribeid: string;
    programidx: number;
    subscribeType: number;
    price: number;
    currency: string;
    ticketName: string;
  };
  onCancel: () => void;
}

const UnsubscribeModal = ({ onCancel, unSubScribeData, open }: UnsubscribeModalProps) => {
  const [selectReason, setSelectReason] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(0);
  const [reasonText, setReasonText] = useState('');

  const setModalInformation = useSetAtom(modalInformationAtom);
  const userInfor = useAtomValue(userInforAtom);
  const companyInfor = useAtomValue(companyInforAtom);

  const { subscribeid, programidx, subscribeType, price, currency, ticketName } = unSubScribeData;

  const dataSource = [
    {
      userInfor: '멤버십',
      contents: <div>{ticketName}</div>,
      key: 1
    },
    {
      userInfor: '멤버십 상세',
      contents: '기본이용권',
      key: 2
    },
    {
      userInfor: '금액',
      contents: <div>{price.toLocaleString()}원</div>,
      key: 3
    }
  ];

  const handleChangeReasonInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setReasonText(value);
  };

  const equipmentData = [
    { label: '사용을 잘 하지 않아서', value: 1 },
    { label: '이용 요금이 비싸서', value: 2 },
    { label: '다른 이용권으로 변경을 원해서', value: 3 },
    { label: '엑스바디 멤버십을 통해 기대했던 만큼 효과를 보지 못해서', value: 4 },
    {
      label: (
        <RadioInputWrapper>
          <div>기타</div>
          <DefaultInput
            name="reason"
            value={reasonText}
            onChange={handleChangeReasonInput}
            height="small"
            disabled={selectedEquipment !== 5}
          />
        </RadioInputWrapper>
      ),
      value: 5
    }
  ];

  const requestUnSubScribe = useMutation({
    mutationFn: (params: any) =>
      restfetcher({
        method: 'POST',
        path: 'biz/my/membership/unsubscribe',
        params: params
      }),
    onSuccess: (data: any) => {
      if (data?.result === 'success') {
        setModalInformation({
          type: 'UNSUBSCRIBE_SUCCESS',
          handleClickRightBtn: () => {
            window.location.reload();
          }
        });
      }
    }
  });

  const handleClickRequestOneMore = () => {
    setModalInformation({
      type: 'MEMBERSHIP_UNSUBSCRIBE',
      handleClickRightBtn: () => {
        const params = {
          // usridx: 249,
          // companyidx: 291,
          usridx: userInfor.usridx,
          companyidx: companyInfor.companyidx,
          subscribeid: subscribeid,
          reason: selectedEquipment,
          etc: reasonText
        };

        requestUnSubScribe.mutate(params);
      }
    });
  };

  const handleRadioChange = (value: number) => {
    setSelectedEquipment(value);
  };

  const handleClickSelectReason = () => {
    setSelectReason(true);
  };

  useEffect(() => {
    if (selectReason && !open) {
      setSelectReason(false);
    }
  }, [open, selectReason]);

  return (
    <Modal open={open} onCancel={onCancel} footer={null} centered>
      <Wrapper>
        <Title>멤버십 해지하기</Title>
        <div>
          {selectReason ? (
            <InforText>왜 구독을 해지하시나요? (선택사항)</InforText>
          ) : (
            <InforText>선택한 멤버십구독을 해지하시겠습니까?</InforText>
          )}
          {selectReason ? (
            <RadioWrapper>
              <RadioButton
                value={selectedEquipment}
                options={equipmentData}
                onChange={handleRadioChange}
              />
            </RadioWrapper>
          ) : (
            <Table
              columns={commonSubScribeColumns}
              dataSource={dataSource}
              bordered
              pagination={false}
              showHeader={false}
            />
          )}
        </div>
        <ButtonWrapper>
          <DefaultButton onClick={onCancel} text="닫기" />
          {selectReason ? (
            <DefaultButton
              onClick={handleClickRequestOneMore}
              type="primary"
              text="계속 해지 진행"
            />
          ) : (
            <DefaultButton onClick={handleClickSelectReason} type="primary" text="다음" />
          )}
        </ButtonWrapper>
      </Wrapper>
    </Modal>
  );
};

export default UnsubscribeModal;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  .ant-table-tbody {
    tr td {
      padding: 15px 12px;
      ${font.caption1_regular};

      &:first-of-type {
        background: ${color.gray100};
        text-align: center;
        color: ${color.gray600};
        ${font.caption1_regular};
      }

      &:last-of-type {
        color: ${color.black2};
        ${font.caption1_regular};
      }
    }
  }
`;

const Title = styled.h4`
  color: ${color.black2};
  ${font.h4_medium};
  text-align: center;
`;

const InforText = styled.p`
  padding-bottom: 16px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;

  p {
    display: flex;
    justify-content: center;
    button {
      width: 160px;
      border-color: ${color.gray500} !important;
      color: ${color.gray500};
    }
  }
`;

const RadioWrapper = styled.div`
  padding: 16px;
  border-radius: 8px;
  background: ${color.gray100};

  .ant-radio-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const RadioInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
